import {
  Box,
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
  MediaPlayer,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import type { OpeningHours } from 'lib-shared-types-api';
import { useCallback, useMemo, useState } from 'react';
import { useMeasure } from 'react-use';

import { ListItemHeader } from 'components/settings/components/sections/openingHours/components/listItemHeader/listItemHeader';
import { useScheduleContext } from 'components/settings/components/sections/openingHours/views/schedule/schedule.context';
import { getUserDisplayName, useCurrentUser } from 'utils/user';

import { ListItemComponentProps } from '../../listItem';
import { useListItemContext } from '../../listItem.context';
import styles from '../../listItem.module.scss';
import { OverriddenItem } from '../overriddenItem/overriddenItem';

import { AnnouncementSelect } from './components/announcementSelect/announcementSelect';
import {
  AudioMessageModal,
  AudioMessageModalType,
} from './components/audioMessageModal/audioMessageModal';

function getModalTitle({
  activeModal,
  name,
  type,
}: {
  activeModal: AudioMessageModalType;
  name: string;
  type: OpeningHours['opening_hour_type'];
}): string {
  switch (activeModal) {
    case 'record':
      return type === 'regular'
        ? 'Record standard audio message'
        : `Record audio message for ${name}`;
    default:
      return type === 'regular'
        ? 'Upload standard audio messsage'
        : `Upload audio message for ${name}`;
  }
}

export const AudioMessage = ({
  hasConflict,
  name: propName,
  openingHours,
  overrideException,
}: ListItemComponentProps): JSX.Element => {
  const {
    announcement,
    duration,
    id,
    name,
    next_date,
    opening_hour_type: openingHourType,
    recurring,
  } = openingHours;

  const [activeModal, setActiveModal] = useState<null | AudioMessageModalType>(
    null,
  );

  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const shouldHideControls = useMemo(() => width <= 840, [width]);

  const { onAudioMessageChange, onAudioMessageClear, onOverriddenChange } =
    useListItemContext();
  const onChange = onAudioMessageChange(id);
  const onClear = onAudioMessageClear?.(id);

  const { refetchAudioMessages } = useScheduleContext();
  const { user } = useCurrentUser();
  const agentName = getUserDisplayName(user);

  const handleModalOpen = useCallback(
    (type?: AudioMessageModalType, toggleDropdown?: VoidFunction) => () => {
      if (type === 'record') {
        ampli.businessHoursScheduleAudioMessageOptionsMenuOptionRecordClicked({
          openingHourType,
        });
      } else {
        ampli.businessHoursScheduleAudioMessageOptionsMenuOptionUploadClicked({
          openingHourType,
        });
      }

      toggleDropdown?.();
      setActiveModal(type || null);
    },
    [openingHourType],
  );

  const handleUploadFinish = useCallback(
    (file: null | File, url: string) => {
      onChange({ url, label: file?.name || '', author: agentName });
      refetchAudioMessages();
      setActiveModal(null);
    },
    [agentName, refetchAudioMessages, onChange],
  );

  const itemName = propName || name;

  return (
    <div className={styles.root} ref={ref}>
      {overrideException ? (
        <OverriddenItem
          openingHours={openingHours}
          overrideException={overrideException}
          onChange={() => onOverriddenChange(openingHours)}
        />
      ) : (
        <div
          className={classNames(styles.item, styles.itemAudioMessage, {
            [styles.itemHasConflict]: hasConflict,
          })}
        >
          <ListItemHeader
            duration={duration}
            hasConflict={hasConflict}
            name={itemName}
            nextDate={next_date}
            openingHourType={openingHourType}
            recurring={recurring}
          />
          <Box className={styles.announcementPicker}>
            <AnnouncementSelect
              onSelect={onChange}
              placeholder="None (standard audio message)"
              selectedAudio={announcement}
            />
          </Box>
          {announcement && (
            <MediaPlayer
              className={styles.mediaPlayer}
              mediaFile={{ src: announcement.url ?? '', type: 'audio' }}
              controls={{
                download: !shouldHideControls,
                volume: !shouldHideControls,
              }}
            />
          )}
        </div>
      )}
      <DropdownButton
        buttonProps={{ variant: 'tertiary' }}
        className={styles.dropdownButton}
        fixed
        icon="dots-h"
        noArrow
        onToggle={(isOpen) => {
          if (isOpen) {
            ampli.businessHoursScheduleAudioMessageOptionsMenuOpened({
              openingHourType,
            });
          }
        }}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ toggleDropdown }) => (
              <ButtonList>
                <ButtonList.Item
                  onClick={handleModalOpen('upload', toggleDropdown)}
                >
                  Upload audio message
                </ButtonList.Item>
                <ButtonList.Item
                  onClick={handleModalOpen('record', toggleDropdown)}
                >
                  Record audio message
                </ButtonList.Item>
                {openingHourType !== 'regular' && onClear && (
                  <ButtonList.Item
                    isDisabled={!announcement}
                    onClick={() => {
                      ampli.businessHoursScheduleAudioMessageOptionsMenuOptionClearClicked(
                        { openingHourType },
                      );
                      onClear();
                      toggleDropdown();
                    }}
                  >
                    Clear
                  </ButtonList.Item>
                )}
              </ButtonList>
            )}
          </DropdownButtonContext.Consumer>
        }
      />
      {/* Modals */}
      {activeModal && (
        <AudioMessageModal
          modalType={activeModal}
          onClose={handleModalOpen()}
          onUploadFinish={handleUploadFinish}
          openingHourType={openingHourType}
          title={getModalTitle({
            type: openingHourType,
            name: itemName,
            activeModal,
          })}
        />
      )}
    </div>
  );
};
